import { compose, get } from 'lodash/fp';
import { DefaultTheme, ThemeProps } from 'styled-components';

import { BasicColorSet, ColorSet } from '@optimus-web/local-tokens';

import { Sized, Variant } from './duplicatedTypes';
import { alpha } from './duplicatedUtils';
import { Heights, Palette, Size, Spacing } from './types';

export const getColors = get('theme.palette');

export const getDefaultColor = compose(get('default'), getColors);

export const getDefaultColorAccent = (accent: keyof BasicColorSet) => compose(get(accent), getDefaultColor);

export const getDefaultBaseColor = getDefaultColorAccent('base');
export const getDefaultBackgroundColor = getDefaultColorAccent('background');
export const getDefaultBackgroundDarkColor = getDefaultColorAccent('backgroundDark');
export const getDefaultBackgroundLightColor = getDefaultColorAccent('backgroundLight');
export const getDefaultBackgroundSecondaryColor = getDefaultColorAccent('backgroundSecondary');
export const getDefaultBackgroundTertiaryColor = getDefaultColorAccent('backgroundTertiary');

export const getDefaultForegroundColor = getDefaultColorAccent('foreground');

export const getDefaultTextColor = getDefaultColorAccent('text');
export const getDefaultTextSecondaryColor = getDefaultColorAccent('textSecondary');
export const getDefaultTextContrastColor = getDefaultColorAccent('textContrast');
export const getDefaultTextSecondaryContrastColor = getDefaultColorAccent('textSecondaryContrast');

export const getDefaultHoverColor = getDefaultColorAccent('hover');
export const getDefaultHoverLightColor = getDefaultColorAccent('hoverLight');

export const getDefaultFocusColor = getDefaultColorAccent('focus');
export const getDefaultFocusLightColor = getDefaultColorAccent('focusLight');

export const getDefaultActiveColor = getDefaultColorAccent('active');
export const getDefaultActiveLightColor = getDefaultColorAccent('activeLight');

export const getDefaultLabelColor = getDefaultColorAccent('label');
export const getDefaultPlaceholderColor = getDefaultColorAccent('placeholder');
export const getDefaultBorderColor = getDefaultColorAccent('border');
export const getDefaultImageBackground = getDefaultColorAccent('imageBackground');
export const getDefaultImagePlaceholderColor = getDefaultColorAccent('imagePlaceholder');

export const getPrimaryColor = compose(get('primary'), getColors);

export const getPrimaryColorAccent = (accent: keyof ColorSet) => compose(get(accent), getPrimaryColor);

export const getPrimaryBaseColor = getPrimaryColorAccent('base');
export const getPrimaryBackgroundColor = getPrimaryColorAccent('background');
export const getPrimaryBackgroundLightColor = getPrimaryColorAccent('backgroundLight');
export const getPrimaryBackgroundSecondaryColor = getPrimaryColorAccent('backgroundSecondary');
export const getPrimaryHoverColor = getPrimaryColorAccent('hover');
export const getPrimaryFocusColor = getPrimaryColorAccent('focus');
export const getPrimaryTextColor = getPrimaryColorAccent('text');
export const getPrimaryTextContrastColor = getPrimaryColorAccent('textContrast');
export const getPrimaryTextSecondaryContrastColor = getPrimaryColorAccent('textSecondaryContrast');
export const getPrimaryBorderColor = getPrimaryColorAccent('border');
export const getPrimaryActiveColor = getPrimaryColorAccent('active');
export const getPrimaryActiveLightColor = getPrimaryColorAccent('activeLight');
export const getPrimaryFocusLightColor = getPrimaryColorAccent('focusLight');
export const getPrimaryHoverLightColor = getPrimaryColorAccent('hoverLight');
export const getPrimaryDataVisualizationBackgroundColor = getPrimaryColorAccent('dataVisualizationBackground');

export const getErrorColor = compose(get('error'), getColors);

export const getErrorColorAccent = (accent: keyof ColorSet) => compose(get(accent), getErrorColor);

export const getErrorBaseColor = getErrorColorAccent('base');
export const getErrorBackgroundColor = getErrorColorAccent('background');
export const getErrorHoverColor = getErrorColorAccent('hover');
export const getErrorFocusColor = getErrorColorAccent('focus');
export const getErrorTextColor = getErrorColorAccent('text');
export const getErrorTextContrastColor = getErrorColorAccent('textContrast');
export const getErrorTextSecondaryContrastColor = getErrorColorAccent('textSecondaryContrast');
export const getErrorBorderColor = getErrorColorAccent('border');
export const getErrorActiveColor = getErrorColorAccent('active');
export const getErrorActiveLightColor = getErrorColorAccent('activeLight');
export const getErrorFocusLightColor = getErrorColorAccent('focusLight');
export const getErrorHoverLightColor = getErrorColorAccent('hoverLight');
export const getErrorBackgroundLightColor = getErrorColorAccent('backgroundLight');
export const getErrorBackgroundSecondaryColor = getErrorColorAccent('backgroundSecondary');
export const getErrorDataVisualizationBackgroundColor = getErrorColorAccent('dataVisualizationBackground');

export const getInfoColor = compose(get('info'), getColors);

export const getInfoColorAccent = (accent: keyof ColorSet) => compose(get(accent), getInfoColor);

export const getInfoBaseColor = getInfoColorAccent('base');
export const getInfoBackgroundColor = getInfoColorAccent('background');
export const getInfoHoverColor = getInfoColorAccent('hover');
export const getInfoFocusColor = getInfoColorAccent('focus');
export const getInfoTextColor = getInfoColorAccent('text');
export const getInfoTextContrastColor = getInfoColorAccent('textContrast');
export const getInfoTextSecondaryContrastColor = getErrorColorAccent('textSecondaryContrast');
export const getInfoBorderColor = getInfoColorAccent('border');
export const getInfoActiveColor = getInfoColorAccent('active');
export const getInfoActiveLightColor = getInfoColorAccent('activeLight');
export const getInfoFocusLightColor = getInfoColorAccent('focusLight');
export const getInfoHoverLightColor = getInfoColorAccent('hoverLight');
export const getInfoBackgroundLightColor = getInfoColorAccent('backgroundLight');
export const getInfoBackgroundSecondaryColor = getInfoColorAccent('backgroundSecondary');
export const getInfoDataVisualizationBackgroundColor = getInfoColorAccent('dataVisualizationBackground');

export const getSuccessColor = compose(get('success'), getColors);

export const getSuccessColorAccent = (accent: keyof ColorSet) => compose(get(accent), getSuccessColor);

export const getSuccessBaseColor = getSuccessColorAccent('base');
export const getSuccessBackgroundColor = getSuccessColorAccent('background');
export const getSuccessHoverColor = getSuccessColorAccent('hover');
export const getSuccessFocusColor = getSuccessColorAccent('focus');
export const getSuccessTextColor = getSuccessColorAccent('text');
export const getSuccessTextContrastColor = getSuccessColorAccent('textContrast');
export const getSuccessTextSecondaryContrastColor = getSuccessColorAccent('textSecondaryContrast');
export const getSuccessBorderColor = getSuccessColorAccent('border');
export const getSuccessActiveColor = getSuccessColorAccent('active');
export const getSuccessActiveLightColor = getSuccessColorAccent('activeLight');
export const getSuccessFocusLightColor = getSuccessColorAccent('focusLight');
export const getSuccessHoverLightColor = getSuccessColorAccent('hoverLight');
export const getSuccessBackgroundLightColor = getSuccessColorAccent('backgroundLight');
export const getSuccessBackgroundSecondaryColor = getSuccessColorAccent('backgroundSecondary');
export const getSuccessDataVisualizationBackgroundColor = getSuccessColorAccent('dataVisualizationBackground');

export const getDisabledColor = compose(get('disabled'), getColors);

export const getDisabledColorAccent = (accent: keyof ColorSet) => compose(get(accent), getDisabledColor);

export const getDisabledBaseColor = getDisabledColorAccent('base');
export const getDisabledBackgroundColor = getDisabledColorAccent('background');
export const getDisabledHoverColor = getDisabledColorAccent('hover');
export const getDisabledFocusColor = getDisabledColorAccent('focus');
export const getDisabledTextColor = getDisabledColorAccent('text');
export const getDisabledTextContrastColor = getDisabledColorAccent('textContrast');
export const getDisabledTextSecondaryContrastColor = getDisabledColorAccent('textSecondaryContrast');
export const getDisabledBorderColor = getDisabledColorAccent('border');
export const getDisabledActiveColor = getDisabledColorAccent('active');
export const getDisabledActiveLightColor = getDisabledColorAccent('activeLight');
export const getDisabledFocusLightColor = getDisabledColorAccent('focusLight');
export const getDisabledHoverLightColor = getDisabledColorAccent('hoverLight');
export const getDisabledBackgroundLightColor = getDisabledColorAccent('backgroundLight');
export const getDisabledBackgroundSecondaryColor = getDisabledColorAccent('backgroundSecondary');

export const getWarningColor = compose(get('warning'), getColors);

export const getWarningColorAccent = (accent: keyof ColorSet) => compose(get(accent), getWarningColor);

export const getWarningBaseColor = getWarningColorAccent('base');
export const getWarningBackgroundColor = getWarningColorAccent('background');
export const getWarningBackgroundLightColor = getWarningColorAccent('backgroundLight');
export const getWarningBackgroundSecondaryColor = getWarningColorAccent('backgroundSecondary');
export const getWarningHoverColor = getWarningColorAccent('hover');
export const getWarningFocusColor = getWarningColorAccent('focus');
export const getWarningTextColor = getWarningColorAccent('text');
export const getWarningTextContrastColor = getWarningColorAccent('textContrast');
export const getWarningTextSecondaryContrastColor = getWarningColorAccent('textSecondaryContrast');
export const getWarningBorderColor = getWarningColorAccent('border');
export const getWarningActiveColor = getWarningColorAccent('active');
export const getWarningActiveLightColor = getWarningColorAccent('activeLight');
export const getWarningFocusLightColor = getWarningColorAccent('focusLight');
export const getWarningHoverLightColor = getWarningColorAccent('hoverLight');
export const getWarningDataVisualizationBackgroundColor = getWarningColorAccent('dataVisualizationBackground');

export const getBaseColor = (c: ColorSet) => c.base;
export const getBackgroundColor = (c: ColorSet) => c.background;
export const getHoverColor = (c: ColorSet) => c.hover;
export const getHoverLightColor = (c: ColorSet) => c.hoverLight;
export const getFocusColor = (c: ColorSet) => c.focus;
export const getFocusLightColor = (c: ColorSet) => c.focusLight;
export const getActiveColor = (c: ColorSet) => c.active;
export const getActiveLightColor = (c: ColorSet) => c.activeLight;
export const getTextColor = (c: ColorSet) => c.text;
export const getTextContrastColor = (c: ColorSet) => c.textContrast;
export const getTextSecondaryContrastColor = (c: ColorSet) => c.textSecondaryContrast;
export const getBorderColor = (c: ColorSet) => c.border;
export const getBackgroundLightColor = (c: ColorSet) => c.backgroundLight;
export const getBackgroundSecondaryColor = (c: ColorSet) => c.backgroundSecondary;
export const getBannerSubtleBackgroundColor = (c: ColorSet) => alpha(c.base, 0.08);

type GetPalette<T, A> = T extends A ? T : never;
type PaletteVariant = GetPalette<keyof Palette, Variant>;

interface ApplyColorOptions extends ThemeProps<DefaultTheme> {
    variant?: PaletteVariant;
}

export const applyColors =
    (cssColorProperties: TemplateStringsArray, ...colorGetters: Array<((c: ColorSet) => string) | string>) =>
    ({ variant = 'default', theme }: ApplyColorOptions) =>
        cssColorProperties.reduce((appliedColors, cssColorProperty, index) => {
            let interpolation: string;
            if (colorGetters[index]) {
                const colorGetter = colorGetters[index];
                const value = typeof colorGetter === 'function' ? colorGetter(theme.palette[variant]) : colorGetter;
                interpolation = `${cssColorProperty}${value}`;
            } else {
                interpolation = cssColorProperty;
            }

            return `${appliedColors}${interpolation}`;
        }, '');

const getSpacing = (type: keyof Spacing) => compose(get(type), get('theme.spacing'));

export const getHorizontalInputPadding = getSpacing('inputHorizontal');
export const getSmallHorizontalInputPadding = (props: ThemeProps<DefaultTheme>) =>
    getSpacing('inputHorizontal')(props) / 2;

const getVariableHeight = (element: keyof Size, height: keyof Heights) =>
    compose<ThemeProps<DefaultTheme>, number, Heights, Size>(get(height), get([element, 'heights']), get('theme.size'));

export const getSmallButtonHeight = getVariableHeight('button', 'small');
export const getMediumButtonHeight = getVariableHeight('button', 'medium');
export const getLargeButtonHeight = getVariableHeight('button', 'large');

export const getSmallInputHeight = getVariableHeight('input', 'small');
export const getMediumInputHeight = getVariableHeight('input', 'medium');
export const getLargeInputHeight = getVariableHeight('input', 'large');

type GetElementHeightProps = ThemeProps<DefaultTheme> & Sized;

const getElementHeight =
    (element: keyof Size) =>
    ({ size, theme }: GetElementHeightProps) => {
        if (!size) {
            throw Error('No size property was provided');
        }

        return `height: ${getVariableHeight(element, size)({ theme })}px;`;
    };

export const getInputHeight = getElementHeight('input');
export const getButtonHeight = getElementHeight('button');
